import React, { useEffect, useRef, useState } from 'react';
import { MbmProvider } from '@amzn/react-arb-tools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { DEFAULT_LOCALE } from '../constants';
import arbManifest from 'translations/arbManifest';
import { resolveBundleUrlWithFallbacks } from '../helpers';
import ChatPage from "../pages/chat/chat-page";
import ShoulderTapPage from "../pages/shoulder-tap/ShoulderTapPage";
import { useUser } from '../hooks/useUser';
import { useQueryParams } from '../hooks/useQueryParams';
import { useShoulderTapAnalysis } from '../hooks/useShoulderTapAnalysis';
import { isInIframe, isConnectApp, initializeConnectIfNeeded } from '../utils/iframe';
import { isPreProdEnvironment } from '../utils/environment';
import {
    postToHost,
    subscribeFromHost,
    BridgeEventType,
    CopilotContentPayload,
    ServiceHealthChangedPayload,
    ShoulderTapFiredPayload,
} from '../utils/hostBridge';
import {
    buildShoulderTapFiredContent,
    SERVICE_HEALTH_CONTENT,
    SHOULDER_TAP_STATIC_CONTENT,
} from '../content/shoulderTapContent';
import { api, ShoulderTapPayload } from '../services/api';
import IframeAuthDialog from './IframeAuthDialog';
import CenteredMessage from './CenteredMessage';

const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder()
        .withLocale(DEFAULT_LOCALE)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build(),
    resolveBundleUrl: (
        resourcePackPath: string | undefined,
        locale: string,
        bundle: string
    ) => resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
};

const App = () => {
    const { contactId } = useQueryParams();
    const { user, loading, login, logout, error, checkAuthentication } = useUser();
    const inIframe = isInIframe();
    const inConnect = isConnectApp();
    const [connectInitialized, setConnectInitialized] = useState(false);
    const [authAttempts, setAuthAttempts] = useState(0);
    // Customer state is no longer initialized over HTTP (the /initializeCustomerState
    // endpoint was removed). Session setup now happens via the WebSocket flow, so there
    // is no HTTP init phase here.
    const maxAuthAttempts = 2;

    // Initialize Connect SDK if needed
    useEffect(() => {
        if (inIframe && inConnect && !connectInitialized) {
            initializeConnectIfNeeded()
                .then(() => setConnectInitialized(true))
                .catch(console.error);
        }
    }, [inIframe, inConnect, connectInitialized]);

    // Apply iframe full-width class when in iframe
    useEffect(() => {
        if (inIframe) {
            document.body.classList.add('iframe-full-width');
        } else {
            document.body.classList.remove('iframe-full-width');
        }

        return () => {
            document.body.classList.remove('iframe-full-width');
        };
    }, [inIframe]);
    // Handle authentication based on iframe context
    useEffect(() => {
        if (!loading && !user?.authenticated && !inIframe) {
            console.log('User not authenticated, automatically redirecting to login...');
            setTimeout(() => {
                login();
            }, 500);
        }
    }, [loading, user?.authenticated, login, inIframe]);

    // Publish the static copy bundle so the host can render chrome (the
    // collapsed pill, the offline notice) before any tap has fired. Emitted
    // on mount, and again whenever the host asks — a host that attaches its
    // listener after we load, or remounts its overlay, would otherwise miss
    // the initial emission.
    useEffect(() => {
        const publish = () => postToHost<CopilotContentPayload>(
            BridgeEventType.CopilotContent,
            { content: SHOULDER_TAP_STATIC_CONTENT },
        );
        publish();
        return subscribeFromHost(BridgeEventType.ContentRequest, publish);
    }, []);

    // Poll the backend for shoulder-tap events once we have a contactId
    // and the user is authenticated. The host app is expected to pass
    // contactId as a query param on the iframe URL. When a tap fires,
    // forward it to the host over the postMessage bridge — the host
    // renders the accept/dismiss UI and calls back into us via the
    // same bridge (see subscribe hooks below).
    const shoulderTapContactId = user?.authenticated ? contactId : undefined;
    const { tap: shoulderTap, isOffline: shoulderTapOffline } =
        useShoulderTapAnalysis(shoulderTapContactId);

    // Every tap in the current sequence, oldest first. Copy resolution (the
    // read-to-customer script) depends on the whole sequence rather than the
    // latest tap: a tap arriving after the agent accepted can change which
    // script applies. A dismiss ends the sequence, so we clear on the host's
    // dismiss and on contact change.
    const tapSequenceRef = useRef<ShoulderTapPayload[]>([]);

    useEffect(() => {
        tapSequenceRef.current = [];
    }, [shoulderTapContactId]);

    useEffect(() => {
        if (!shoulderTap || !shoulderTapContactId) return;
        tapSequenceRef.current = [...tapSequenceRef.current, shoulderTap];
        const content = buildShoulderTapFiredContent(tapSequenceRef.current);
        console.log('[shoulder-tap] fired', { contactId: shoulderTapContactId, tap: shoulderTap });
        postToHost<ShoulderTapFiredPayload>(BridgeEventType.ShoulderTapFired, {
            contactId: shoulderTapContactId,
            tap: shoulderTap,
            content,
        });
    }, [shoulderTap, shoulderTapContactId]);

    // Forward degraded/offline transitions to the host so it can render
    // an "unavailable" indicator (BRD AI-04). We only emit once the
    // hook has an actual observation — null means "not yet determined"
    // and telling the host we're healthy before verifying would cause
    // a healthy → offline flicker if the first request fails.
    useEffect(() => {
        if (!shoulderTapContactId || shoulderTapOffline === null) return;
        const status = shoulderTapOffline ? 'offline' : 'healthy';
        console.log('[shoulder-tap] service health', status);
        postToHost<ServiceHealthChangedPayload>(BridgeEventType.ServiceHealthChanged, {
            status,
            content: SERVICE_HEALTH_CONTENT,
        });
    }, [shoulderTapOffline, shoulderTapContactId]);

    // Host-driven accept/dismiss: the host UI owns the buttons; we own
    // the API calls. The effect re-subscribes whenever
    // shoulderTapContactId changes so callbacks always target the
    // current contact.
    useEffect(() => {
        if (!shoulderTapContactId) return;
        const unsubscribeAccept = subscribeFromHost(BridgeEventType.ShoulderTapAccept, () => {
            api.acceptShoulderTap(shoulderTapContactId);
        });
        const unsubscribeDismiss = subscribeFromHost(BridgeEventType.ShoulderTapDismiss, () => {
            // Dismiss ends the sequence: the next tap starts a fresh one, so
            // its script resolves without the dismissed taps.
            tapSequenceRef.current = [];
            api.dismissShoulderTap(shoulderTapContactId);
        });
        return () => {
            unsubscribeAccept();
            unsubscribeDismiss();
        };
    }, [shoulderTapContactId]);

    const handleIframeAuthComplete = async () => {
        // Re-check authentication after popup closes
        await checkAuthentication();
        setAuthAttempts(prev => prev + 1);
    };

    // TODO: replace with spinners
    if (loading) {
        return <CenteredMessage message="Loading..." />;
    }

    if (!user?.authenticated) {
        if (inIframe && authAttempts < maxAuthAttempts) {
            return <IframeAuthDialog onAuthComplete={handleIframeAuthComplete} authAttempts={authAttempts} />;
        }
        
        if (inIframe && authAttempts >= maxAuthAttempts) {
            return <CenteredMessage message="Quick Network Hiccup! Try this first: Refresh your browser || Still seeing this? Please Share Contact ID with any helpful context [here](https://t.corp.amazon.com/V2004379452/communication)" />;
        }
        
        return <CenteredMessage message="Redirecting to login..." />;
    }

    return (
        <MbmProvider {...mbmOptions}>
            {window.location.pathname === '/shoulder-tap' && isPreProdEnvironment() ? (
                <ShoulderTapPage user={user} />
            ) : (
                <ChatPage
                    user={user}
                    contactId={contactId}
                />
            )}
        </MbmProvider>
    );
};

export default App;
